import { shaderMaterial, useFBO } from "@react-three/drei";
import { extend, useFrame, useThree } from "@react-three/fiber";
import React, { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";

/**
 * Two-pass full-screen pipeline:
 * - Pass 1: Procedural gradient fragment shader renders into an offscreen FBO.
 * - Pass 2: ASCII color post-process samples the FBO and composites glyphs to the screen.
 * Uses drei's shaderMaterial for typed uniforms and useFBO for render-to-texture.
 */

// --- ASCII Atlas Generation ---

const ASCII_GLYPHS = " ■ ◆ ◼ ▲ ▼ ● █ ▂▃▄▅▆▇▏▎▍▌▋▊▉";
const ALT_ASCII_GLYPHS = "■□◆◼▲▼●○ █▁▂▃▄▅▆▇▏▎▍▌▋▊▉";

/**
 * Creates a texture atlas for the ASCII glyphs.
 * @param {string} glyphs - A string of characters to include in the atlas.
 * @returns {object} An object containing the texture, grid dimensions, and glyph count.
 */
function createAsciiAtlasTexture(glyphs: string) {
  const numGlyphs = glyphs.length;
  const cols = Math.ceil(Math.sqrt(numGlyphs));
  const rows = Math.ceil(numGlyphs / cols);

  const glyphSize = 64; // Texture size for each glyph
  const padding = 8; // Padding around each glyph

  const canvas = document.createElement("canvas");
  canvas.width = cols * glyphSize;
  canvas.height = rows * glyphSize;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not get 2D context for ASCII atlas");
  }

  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.fillStyle = "#fff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  // Using 'monospace' ensures glyphs are centered reasonably well
  ctx.font = `${glyphSize - padding * 2}px monospace`;

  for (let i = 0; i < numGlyphs; i++) {
    const char = glyphs[i];
    const x = i % cols;
    const y = Math.floor(i / cols);

    const canvasX = x * glyphSize + glyphSize / 2;
    // Slight vertical offset for better centering in some browsers
    const canvasY = y * glyphSize + glyphSize / 2 + 2;

    ctx.fillText(char, canvasX, canvasY);
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.flipY = true; // Match GLSL coordinates
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.needsUpdate = true;

  return { texture, cols, rows, numGlyphs };
}

// --- Shaders ---

// Vertex shader for both passes (full-screen quad)
// Note: Built-in geometries provide the 'position' attribute; do not redeclare it here.
const FullscreenVertexShader = `
precision highp float;
precision highp int;

varying vec2 vUv;
void main() {
    gl_Position = vec4(position, 1.0);
    vUv = gl_Position.xy * 0.5 + 0.5;
}
`;

// Fragment shader for the gradient (from provided source, 'Su')
// Produces layered color bands with noise-driven rotation and wave distortion,
// then applies temperature/hue/saturation, levels, contrast/brightness, gamma, and grain.
const GradientFragmentShader = `
precision mediump float;

uniform float uTime;
uniform vec2  uResolution;

uniform float uAnimSpeed;
uniform float uNoiseSpeed;
uniform float uRotationAmount;

uniform float uWaveFreq;
uniform float uWaveAmp;
uniform float uWaveSpeed;
uniform float uWaveFreqY;
uniform float uWaveAmpY;

uniform vec3 uColor1;
uniform vec3 uColor2;
uniform vec3 uColor3;
uniform vec3 uColor4;

uniform float uGradientAngle;
uniform float uGradientSmooth1;
uniform float uGradientSmooth2;
uniform float uGradientPos1;
uniform float uGradientPos2;
uniform float uGradientMixPos;

uniform float uDarkScale1;
uniform float uDarkScale2;
uniform float uDarkScale3;
uniform float uDarkSpeed1;
uniform float uDarkSpeed2;
uniform float uDarkSpeed3;
uniform float uDarkIntensity1;
uniform float uDarkIntensity2;
uniform float uDarkIntensity3;
uniform float uDarkPulseSpeed;
uniform float uDarkPulseAmount;
uniform float uDarkThreshold1;
uniform float uDarkThreshold2;
uniform float uDarkThreshold3;
uniform float uDarkPower1;
uniform float uDarkPower2;
uniform float uDarkPower3;

uniform vec3 uShadowColor;

uniform float uGrainAmount;
uniform float uGrainSize;
uniform vec2  uGrainSeed; // Not used in this shader, but part of the settings

uniform float uSaturation;
uniform float uTemperature;
uniform float uHue;
uniform float uGamma;
uniform float uBrightness;
uniform float uContrast;
uniform float uBlackLevel;
uniform float uWhiteLevel;

varying vec2 vUv;

mat2 Rot(float a) { float s = sin(a); float c = cos(a); return mat2(c, -s, s, c); }
vec2 hash(vec2 p) { p = vec2(dot(p, vec2(2127.1, 81.17)), dot(p, vec2(1269.5, 283.37))); return fract(sin(p) * 43758.5453); }
float noise(in vec2 p) {
    vec2 i = floor(p); vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    float n = mix(mix(dot(-1.0 + 2.0 * hash(i + vec2(0.0)), f - vec2(0.0)),
                          dot(-1.0 + 2.0 * hash(i + vec2(1.0, 0.0)), f - vec2(1.0, 0.0)), u.x),
                      mix(dot(-1.0 + 2.0 * hash(i + vec2(0.0, 1.0)), f - vec2(0.0, 1.0)),
                          dot(-1.0 + 2.0 * hash(i + vec2(1.0, 1.0)), f - vec2(1.0, 1.0)), u.x), u.y);
    return 0.5 + 0.5 * n;
}
vec3 mod289(vec3 x){return x-floor(x*(1.0/289.0))*289.0;} vec2 mod289(vec2 x){return x-floor(x*(1.0/289.0))*289.0;}
vec3 permute(vec3 x){return mod289(((x*34.0)+1.0)*x);}            
float snoise(vec2 v){
    const vec4 C=vec4(0.211324865405187,0.366025403784439,-0.577350269189626,0.024390243902439);
    vec2 i=floor(v+dot(v,C.yy));
    vec2 x0=v-i+dot(i,C.xx);
    vec2 i1=(x0.x>x0.y)?vec2(1.0,0.0):vec2(0.0,1.0);
    vec4 x12=x0.xyxy+C.xxzz; x12.xy-=i1;
    i=mod289(i);
    vec3 p=permute(permute(i.y+vec3(0.0,i1.y,1.0))+i.x+vec3(0.0,i1.x,1.0));
    vec3 m=max(0.5-vec3(dot(x0,x0),dot(x12.xy,x12.xy),dot(x12.zw,x12.zw)),0.0);
    m*=m; m*=m;
    vec3 x=2.0*fract(p*C.www)-1.0;
    vec3 h=abs(x)-0.5;
    vec3 ox=floor(x+0.5);
    vec3 a0=x-ox;
    m*=1.79284291400159-0.85373472095314*(a0*a0+h*h);
    vec3 g;
    g.x=a0.x*x0.x+h.x*x0.y;
    g.yz=a0.yz*x12.xz+h.yz*x12.yw;
    return 130.0*dot(m,g);
}
float fbm(vec2 p,float oct){float v=0.0;float amp=0.5;float freq=1.0;for(float i=0.0;i<4.0;i++){if(i>=oct)break;v+=amp*snoise(p*freq);freq*=2.0;amp*=0.5;}return v;}

float hash12(vec2 p){ vec3 p3 = fract(vec3(p.xyx) * 0.1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }

vec3 rgb2hsv(vec3 c) {
    vec4 K = vec4(0.0, -1.0 / 3.0, 2.0 / 3.0, -1.0);
    vec4 p = mix(vec4(c.bg, K.wz), vec4(c.gb, K.xy), step(c.b, c.g));
    vec4 q = mix(vec4(p.xyw, c.r), vec4(c.r, p.yzx), step(p.x, c.r));
    float d = q.x - min(q.w, q.y);
    float e = 1.0e-10;
    return vec3(abs(q.z + (q.w - q.y) / (6.0 * d + e)), d / (q.x + e), q.x);
}

vec3 hsv2rgb(vec3 c) {
    vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
    vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
    return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
}

vec3 adjustSaturation(vec3 color, float saturation) {
    vec3 gray = vec3(dot(color, vec3(0.299, 0.587, 0.114)));
    return mix(gray, color, saturation);
}

vec3 adjustTemperature(vec3 color, float temp) {
    // Simplified temperature adjustment
    vec3 warm = vec3(1.0 + temp * 0.1, 1.0, 1.0 - temp * 0.2);
    vec3 cool = vec3(1.0 + temp * 0.2, 1.0, 1.0 - temp * 0.1);
    return color * (temp > 0.0 ? warm : cool);
}

vec3 adjustHue(vec3 color, float hueShift) {
    vec3 hsv = rgb2hsv(color);
    hsv.x = fract(hsv.x + hueShift / 360.0);
    return hsv2rgb(hsv);
}

vec3 adjustLevels(vec3 color, float blackLevel, float whiteLevel) {
    return (color - blackLevel) / max(1e-5, (whiteLevel - blackLevel));
}

void main() {
    vec2 uv = vUv;
    float ratio = uResolution.x / uResolution.y;
    vec2 tuv = uv - 0.5;

    
    float deg = noise(vec2(uTime * uNoiseSpeed, tuv.x * tuv.y));
    tuv.y *= 1.0 / ratio;
    highp float rotAngle = radians((deg - 0.5) * uRotationAmount + 180.0);
    tuv *= Rot(rotAngle);
    tuv.y *= ratio;
    
    
    highp float spd = uTime * uWaveSpeed;
    tuv.x += sin(tuv.y * uWaveFreq + spd) / uWaveAmp;
    tuv.y += sin(tuv.x * uWaveFreqY + spd) / uWaveAmpY;

    
    highp float gradA = radians(uGradientAngle);
    mat2 rot = Rot(gradA);
    float gx = (tuv * rot).x;
    vec3 layer1 = mix(uColor1, uColor2, smoothstep(uGradientPos1, uGradientPos1 + uGradientSmooth1, gx));
    vec3 layer2 = mix(uColor3, uColor4, smoothstep(uGradientPos2, uGradientPos2 + uGradientSmooth2, gx));
    float t = 1.0 - smoothstep(uGradientMixPos - 0.8, uGradientMixPos, tuv.y);
    vec3 color = mix(layer1, layer2, t);

    
    color = adjustTemperature(color, uTemperature);
    color = adjustHue(color, uHue);
    color = adjustSaturation(color, uSaturation);
    color = adjustLevels(color, uBlackLevel, uWhiteLevel);
    color += uBrightness;
    color = (color - 0.5) * uContrast + 0.5;
    color = pow(max(color, 0.0), vec3(1.0 / uGamma));
    color = clamp(color, 0.0, 1.0);

    
    vec2 st = uv * 2.0 - 1.0; st.x *= ratio;
    highp float time = uTime * uAnimSpeed;
    
    
    float dn = fbm(st * uDarkScale1 + vec2(time * uDarkSpeed1, -time * uDarkSpeed1 * 0.7), 2.0);
    float pulse = sin(time * uDarkPulseSpeed) * uDarkPulseAmount;
    float dark = smoothstep(uDarkThreshold1 + pulse, uDarkThreshold1 + 0.8 + pulse, dn);
    dark = pow(dark, uDarkPower1);
    color = mix(uShadowColor, color, mix(1.0, dark, uDarkIntensity1));
    
    
    float ldn = fbm(st * uDarkScale2 + vec2(-time * uDarkSpeed2, time * uDarkSpeed2 * 0.7), 1.0);
    float lp = cos(time * uDarkPulseSpeed * 0.75) * uDarkPulseAmount * 0.7;
    float ld = smoothstep(uDarkThreshold2 + lp, uDarkThreshold2 + 0.6 + lp, ldn);
    ld = pow(ld, uDarkPower2);
    color *= mix(1.0, ld, uDarkIntensity2) * 0.3 + 0.7;
    
    
    float odn = fbm(st * uDarkScale3 + vec2(-time * uDarkSpeed3, time * uDarkSpeed3 * 0.75) + 500.0, 1.0);
    float op = sin(time * uDarkPulseSpeed * 1.5 + 1.57) * uDarkPulseAmount * 0.8;
    float od = smoothstep(uDarkThreshold3 + op, uDarkThreshold3 + 0.6 + op, odn);
    od = pow(od, uDarkPower3);
    color = mix(uShadowColor, color, mix(1.0, od, uDarkIntensity3));

    
    
    float grainScale = mix(1.0, 12.0, clamp(uGrainSize, 0.0, 1.0));
    float grain_noise = (hash12(gl_FragCoord.xy / grainScale) - 0.5) * 2.0; // uGrainSeed removed
    color.rgb += grain_noise * uGrainAmount;
    color = max(color, uShadowColor); // Clamp bottom
    color = clamp(color, 0.0, 1.0);

    gl_FragColor = vec4(color, 1.0);
}
`;

// Fragment shader for ASCII color post-processing (from provided source, 'Au')
// Samples the gradient texture per-cell, maps luminance to a glyph in the atlas,
// and mixes foreground (src color) with background per glyph coverage.
const AsciiColorFragmentShader = `
precision highp float;
precision highp int;

uniform sampler2D uScene;
uniform vec2 uResolution;
uniform float uCellSize;
uniform sampler2D uAsciiAtlas;
uniform vec2 uAsciiAtlasGrid;
uniform int uAsciiNumGlyphs;
uniform int uAsciiInvert;
uniform int uAsciiActiveCount;
uniform float uAsciiGamma;
uniform float uAsciiContrast;
uniform float uAsciiBrightness;
uniform float uAsciiRangeMin;
uniform float uAsciiRangeMax;
uniform float uAsciiShift;
uniform vec3 uSecondaryColor;
uniform int uAsciiSwapColors;
varying vec2 vUv;

float luma(vec3 c) { return dot(c, vec3(0.299, 0.587, 0.114)); }

vec4 sampleAscii(vec2 uv, bool withColor) {
  // Calculate cell UV, index, and local coordinates
  vec2 cellUv = uv * uResolution / uCellSize;
  vec2 cellIndex = floor(cellUv);
  vec2 cellLocal = fract(cellUv);

  // Sample center of cell (with 4-tap average)
  vec2 cellCenterUv = (cellIndex + 0.5) * uCellSize / uResolution;
  vec2 offs = (uCellSize / uResolution) * 0.25;
  vec3 c0 = texture2D(uScene, cellCenterUv + vec2(-offs.x, -offs.y)).rgb;
  vec3 c1 = texture2D(uScene, cellCenterUv + vec2( offs.x, -offs.y)).rgb;
  vec3 c2 = texture2D(uScene, cellCenterUv + vec2(-offs.x,  offs.y)).rgb;
  vec3 c3 = texture2D(uScene, cellCenterUv + vec2( offs.x,  offs.y)).rgb;
  vec3 srcColor = (c0 + c1 + c2 + c3) * 0.25;

  // Calculate luminance and apply adjustments
  float l = luma(srcColor);
  float denom = max(1e-5, uAsciiRangeMax - uAsciiRangeMin);
  l = (l - uAsciiRangeMin) / denom;
  l = (l - 0.5) * uAsciiContrast + 0.5 + uAsciiBrightness;
  l = pow(clamp(l, 1e-3, 1.0 - 1e-3), max(1e-5, uAsciiGamma));
  if (uAsciiInvert == 1) { l = 1.0 - l; }

  // Map luminance to glyph index
  float Nf = max(1.0, float(uAsciiNumGlyphs));
  float Nactive = clamp(float(uAsciiActiveCount), 1.0, Nf);
  float fIndex = floor(clamp(l, 0.0, 0.9999) * (Nactive));
  fIndex = clamp(fIndex, 0.0, Nactive - 1.0);
  float shift = mod(floor(uAsciiShift + 0.5), Nactive);
  float fIndexShifted = mod(fIndex + shift, Nactive);

  // Find glyph in atlas
  float cols = max(1.0, uAsciiAtlasGrid.x);
  float rows = max(1.0, uAsciiAtlasGrid.y);
  float gi = mod(fIndexShifted, cols);
  float gj = floor(fIndexShifted / cols);

  vec2 grid = vec2(cols, rows);
  vec2 glyphSize = 1.0 / grid;
  vec2 glyphOrigin = vec2(gi, gj) * glyphSize;
  vec2 glyphUv = glyphOrigin + vec2(cellLocal.x, cellLocal.y) * glyphSize;
  float glyphMask = texture2D(uAsciiAtlas, glyphUv).r;

  // Mix colors
  vec3 fg = srcColor; // Use source color for foreground
  vec3 bg = uSecondaryColor;
  if (uAsciiSwapColors == 1) { vec3 t = fg; fg = bg; bg = t; }
  vec3 outColor = mix(bg, fg, glyphMask);
  return vec4(outColor, 1.0);
}

void main() {
  gl_FragColor = sampleAscii(vUv, true);
}
`;

// --- Shader Materials ---

// Helper to convert 0-255 {r,g,b} to THREE.Color
const toColor = (c: { r: number; g: number; b: number }) => new THREE.Color(c.r / 255, c.g / 255, c.b / 255);

// shaderMaterial returns classes (not instances). We create instances below and mutate their uniforms per-frame.
const GradientMaterial = shaderMaterial(
  {
    uTime: 0,
    uResolution: new THREE.Vector2(),
    uAnimSpeed: 0,
    uNoiseSpeed: 0,
    uRotationAmount: 0,
    uWaveFreq: 0,
    uWaveAmp: 0,
    uWaveSpeed: 0,
    uWaveFreqY: 0,
    uWaveAmpY: 0,
    uColor1: new THREE.Color(),
    uColor2: new THREE.Color(),
    uColor3: new THREE.Color(),
    uColor4: new THREE.Color(),
    uGradientAngle: 0,
    uGradientSmooth1: 0,
    uGradientSmooth2: 0,
    uGradientPos1: 0,
    uGradientPos2: 0,
    uGradientMixPos: 0,
    uDarkPulseSpeed: 0,
    uDarkPulseAmount: 0,
    uDarkScale1: 0,
    uDarkSpeed1: 0,
    uDarkIntensity1: 0,
    uDarkThreshold1: 0,
    uDarkPower1: 0,
    uDarkScale2: 0,
    uDarkSpeed2: 0,
    uDarkIntensity2: 0,
    uDarkThreshold2: 0,
    uDarkPower2: 0,
    uDarkScale3: 0,
    uDarkSpeed3: 0,
    uDarkIntensity3: 0,
    uDarkThreshold3: 0,
    uDarkPower3: 0,
    uShadowColor: new THREE.Color(35/255, 31/255, 32/255),
    uSaturation: 0,
    uTemperature: 0,
    uHue: 0,
    uGamma: 0,
    uBrightness: 0,
    uContrast: 0,
    uBlackLevel: 0,
    uWhiteLevel: 0,
    uGrainAmount: 0,
    uGrainSize: 0,
    uGrainSeed: new THREE.Vector2(),
  },
  FullscreenVertexShader,
  GradientFragmentShader
);

const AsciiMaterial = shaderMaterial(
  {
    uScene: null,
    uResolution: new THREE.Vector2(),
    uCellSize: 0,
    uAsciiAtlas: null,
    uAsciiAtlasGrid: new THREE.Vector2(),
    uAsciiNumGlyphs: 0,
    uAsciiInvert: 0,
    uAsciiActiveCount: 0,
    uAsciiGamma: 0,
    uAsciiContrast: 0,
    uAsciiBrightness: 0,
    uAsciiRangeMin: 0,
    uAsciiRangeMax: 0,
    uAsciiShift: 0,
    uPrimaryColor: new THREE.Color(),
    uSecondaryColor: new THREE.Color(),
    uAsciiSwapColors: 0,
  },
  FullscreenVertexShader,
  AsciiColorFragmentShader
);

extend({ GradientMaterial, AsciiMaterial });

// --- React Component ---

/**
 * Renders a self-contained, animated gradient with an ASCII color post-processing effect.
 * It takes over the full canvas rendering.
 */
export default function AsciiColorGradient(props: {
  animSpeed?: number;
  noiseSpeed?: number;
  rotationAmount?: number;
  waveFreq?: number;
  waveAmp?: number;
  waveSpeed?: number;
  waveFreqY?: number;
  waveAmpY?: number;
  color1?: { r: number; g: number; b: number };
  color2?: { r: number; g: number; b: number };
  color3?: { r: number; g: number; b: number };
  color4?: { r: number; g: number; b: number };
  gradientAngle?: number;
  gradientSmooth1?: number;
  gradientSmooth2?: number;
  gradientPos1?: number;
  gradientPos2?: number;
  gradientMixPos?: number;
  darkPulseSpeed?: number;
  darkPulseAmount?: number;
  darkScale1?: number;
  darkSpeed1?: number;
  darkIntensity1?: number;
  darkThreshold1?: number;
  darkPower1?: number;
  darkScale2?: number;
  darkSpeed2?: number;
  darkIntensity2?: number;
  darkThreshold2?: number;
  darkPower2?: number;
  darkScale3?: number;
  darkSpeed3?: number;
  darkIntensity3?: number;
  darkThreshold3?: number;
  darkPower3?: number;
  shadowColor?: { r: number; g: number; b: number };
  saturation?: number;
  temperature?: number;
  hue?: number;
  gamma?: number;
  brightness?: number;
  contrast?: number;
  blackLevel?: number;
  whiteLevel?: number;
  grainAmount?: number;
  grainSize?: number;
  cellSize?: number;
  asciiInvert?: boolean;
  asciiGamma?: number;
  asciiContrast?: number;
  asciiBrightness?: number;
  asciiRangeMin?: number;
  asciiRangeMax?: number;
  asciiActiveCount?: number;
  asciiShift?: number;
  asciiGlyphSeed?: number;
  asciiPrimaryColor?: { r: number; g: number; b: number };
  asciiSecondaryColor?: { r: number; g: number; b: number };
  asciiSwapColors?: boolean;
  asciiDotMin?: number;
  asciiDotMax?: number;
  asciiDotPower?: number;
  asciiDropLightX?: number;
  asciiDropLightY?: number;
  asciiDropAmbient?: number;
  asciiDropDiffuse?: number;
  asciiDropSpecular?: number;
  asciiDropSpecularPower?: number;
  asciiEnabled?: boolean;
}): React.ReactNode {
  const {
    animSpeed = .8,
    noiseSpeed = 0,
    rotationAmount = 172,
    waveFreq = 3.7,
    waveAmp = 30,
    waveSpeed = 0.037,
    waveFreqY = 8,
    waveAmpY = 50,
    
    color1 = { r: 210, g: 47, b: 11 },
    color2 = { r: 62, g: 132, b: 229 },
    color3 = { r: 100.7, g: 185.1, b: 241.1 },
    color4 = { r: 184.7, g: 207.8, b: 219.4 },
    gradientAngle = 50,
    gradientSmooth1 = 0.4,
    gradientSmooth2 = 0.4,
    gradientPos1 = -0.2,
    gradientPos2 = -0.4,
    gradientMixPos = 0.6,
    darkPulseSpeed = 0.08,
    darkPulseAmount = 0.3,
    darkScale1 = 0.15,
    darkSpeed1 = 0.015,
    darkIntensity1 = 1.1,
    darkThreshold1 = -0.8,
    darkPower1 = 3,
    darkScale2 = 0.1,
    darkSpeed2 = 0.012,
    darkIntensity2 = 0.4,
    darkThreshold2 = -0.4,
    darkPower2 = 0.8,
    darkScale3 = 0.12,
    darkSpeed3 = 0.02,
    darkIntensity3 = 1.2,
    darkThreshold3 = -0.7,
    darkPower3 = 2.5,
    shadowColor = { r: 35, g: 31, b: 32 },
    saturation = 1,
    temperature = -0.2,
    hue = 0,
    gamma = 1,
    brightness = 0,
    contrast = 1.3,
    blackLevel = 0.15,
    whiteLevel = 0.75,
    grainAmount = 0,
    grainSize = 0,
    // ASCII Props
    cellSize = 20,
    asciiInvert = false,
    asciiGamma = 1,
    asciiContrast = .5,
    asciiBrightness = -0.2,
    asciiRangeMin = 0.1,
    asciiRangeMax = 0.8,
    asciiActiveCount = 62,
    asciiShift = 33,
    asciiGlyphSeed = 0, // Note: This prop is not used by the ASCII *color* shader
    asciiPrimaryColor = { r: 255, g: 255, b: 255 },
    asciiSecondaryColor = { r: 35, g: 31, b: 32 },
    asciiSwapColors = true,
    asciiEnabled = true,
    // Ignored props (not used by ASCII Color shader):
    // dotMin, dotMax, dotPower, dropLightX, dropLightY,
    // dropAmbient, dropDiffuse, dropSpecular, dropSpecularPower
  } = props;

  const gl = useThree((state) => state.gl);
  const size = useThree((state) => state.size);
  const clock = useThree((state) => state.clock);

  const gradientRef = useRef<THREE.ShaderMaterial | null>(null);
  const asciiRef = useRef<THREE.ShaderMaterial | null>(null);

  // Create the ASCII atlas texture once
  const asciiAtlas = useMemo(() => createAsciiAtlasTexture(ALT_ASCII_GLYPHS), []);

  // Create scenes and camera
  const [gradientScene, postScene, camera] = useMemo(() => {
    // Isolated scenes ensure clean pass separation (no unintended state carry-over).
    const gScene = new THREE.Scene();
    const pScene = new THREE.Scene();
    const cam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    // Use a basic plane geometry for the full-screen quads
    const planeGeo = new THREE.PlaneGeometry(2, 2);

    // Create gradient quad using the generated material class
    const gMat = new (GradientMaterial as any)();
    gScene.add(new THREE.Mesh(planeGeo, gMat));

    // Create ASCII post-processing quad using the generated material class
    if (asciiEnabled) {
      const aMat = new (AsciiMaterial as any)();
      pScene.add(new THREE.Mesh(planeGeo, aMat));
      asciiRef.current = aMat;
    } else {
      asciiRef.current = null;
    }

    // Store refs to materials for useFrame
    gradientRef.current = gMat;

    return [gScene, pScene, cam];
  }, [asciiEnabled]);

  // Framebuffer for offscreen render using drei's FBO helper
  // The FBO output is fed to the ASCII pass via the uScene sampler2D uniform.
  const fbo = useFBO(size.width, size.height, {
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
    stencilBuffer: false,
    depthBuffer: false,
  });

  // Update FBO and shader resolutions on resize
  useEffect(() => {
    // Maintain pixel-accurate sampling and avoid stretching after viewport changes.
    fbo.setSize(size.width, size.height);
    // Update resolutions in both shaders
    if (gradientRef.current) {
      gradientRef.current.uniforms.uResolution.value.set(
        size.width,
        size.height
      );
    }
    if (asciiRef.current) {
      asciiRef.current.uniforms.uResolution.value.set(size.width, size.height);
    }
  }, [size, fbo]);

  // Update ASCII atlas uniforms when texture is ready
  useEffect(() => {
    if (asciiRef.current) {
      asciiRef.current.uniforms.uAsciiAtlas.value = asciiAtlas.texture;
      asciiRef.current.uniforms.uAsciiAtlasGrid.value.set(
        asciiAtlas.cols,
        asciiAtlas.rows
      );
      asciiRef.current.uniforms.uAsciiNumGlyphs.value = asciiAtlas.numGlyphs;
    }
  }, [asciiAtlas]);

  useFrame(() => {
    if (!gradientRef.current) return;

    // --- Update Gradient Uniforms ---
    // Time and input-controlled modulation for the gradient pass
    const gUniforms = gradientRef.current.uniforms;
    gUniforms.uTime.value = clock.getElapsedTime();
    gUniforms.uAnimSpeed.value = animSpeed;
    gUniforms.uNoiseSpeed.value = noiseSpeed;
    gUniforms.uRotationAmount.value = rotationAmount;
    gUniforms.uWaveFreq.value = waveFreq;
    gUniforms.uWaveAmp.value = waveAmp;
    gUniforms.uWaveSpeed.value = waveSpeed;
    gUniforms.uWaveFreqY.value = waveFreqY;
    gUniforms.uWaveAmpY.value = waveAmpY;
    gUniforms.uColor1.value.copy(toColor(color1));
    gUniforms.uColor2.value.copy(toColor(color2));
    gUniforms.uColor3.value.copy(toColor(color3));
    gUniforms.uColor4.value.copy(toColor(color4));
    gUniforms.uGradientAngle.value = gradientAngle;
    gUniforms.uGradientSmooth1.value = gradientSmooth1;
    gUniforms.uGradientSmooth2.value = gradientSmooth2;
    gUniforms.uGradientPos1.value = gradientPos1;
    gUniforms.uGradientPos2.value = gradientPos2;
    gUniforms.uGradientMixPos.value = gradientMixPos;
    gUniforms.uDarkPulseSpeed.value = darkPulseSpeed;
    gUniforms.uDarkPulseAmount.value = darkPulseAmount;
    gUniforms.uDarkScale1.value = darkScale1;
    gUniforms.uDarkSpeed1.value = darkSpeed1;
    gUniforms.uDarkIntensity1.value = darkIntensity1;
    gUniforms.uDarkThreshold1.value = darkThreshold1;
    gUniforms.uDarkPower1.value = darkPower1;
    gUniforms.uDarkScale2.value = darkScale2;
    gUniforms.uDarkSpeed2.value = darkSpeed2;
    gUniforms.uDarkIntensity2.value = darkIntensity2;
    gUniforms.uDarkThreshold2.value = darkThreshold2;
    gUniforms.uDarkPower2.value = darkPower2;
    gUniforms.uDarkScale3.value = darkScale3;
    gUniforms.uDarkSpeed3.value = darkSpeed3;
    gUniforms.uDarkIntensity3.value = darkIntensity3;
    gUniforms.uDarkThreshold3.value = darkThreshold3;
    gUniforms.uDarkPower3.value = darkPower3;
    gUniforms.uSaturation.value = saturation;
    gUniforms.uTemperature.value = temperature;
    gUniforms.uHue.value = hue;
    gUniforms.uGamma.value = gamma;
    gUniforms.uBrightness.value = brightness;
    gUniforms.uContrast.value = contrast;
    gUniforms.uBlackLevel.value = blackLevel;
    gUniforms.uWhiteLevel.value = whiteLevel;
    gUniforms.uGrainAmount.value = grainAmount;
    gUniforms.uGrainSize.value = grainSize;

    if (asciiRef.current) {
      // --- Update ASCII Uniforms ---
      // Bind offscreen texture and tone-map/glyph-select controls
      const aUniforms = asciiRef.current.uniforms;
      aUniforms.uScene.value = fbo.texture;
      aUniforms.uCellSize.value = cellSize;
      aUniforms.uAsciiInvert.value = asciiInvert ? 1 : 0;
      aUniforms.uAsciiActiveCount.value = Math.max(
        1,
        Math.min(asciiActiveCount, asciiAtlas.numGlyphs)
      );
      aUniforms.uAsciiGamma.value = asciiGamma;
      aUniforms.uAsciiContrast.value = asciiContrast;
      aUniforms.uAsciiBrightness.value = asciiBrightness;
      aUniforms.uAsciiRangeMin.value = asciiRangeMin;
      aUniforms.uAsciiRangeMax.value = asciiRangeMax;
      aUniforms.uAsciiShift.value = asciiShift;
      aUniforms.uPrimaryColor.value.copy(toColor(asciiPrimaryColor)); // This is unused, as shader uses srcColor
      aUniforms.uSecondaryColor.value.copy(toColor(asciiSecondaryColor));
      aUniforms.uAsciiSwapColors.value = asciiSwapColors ? 1 : 0;

      // --- Render Passes ---
      // 1. Render gradient to target
      gl.setRenderTarget(fbo);
      gl.render(gradientScene, camera);

      // 2. Render post-processing to screen
      gl.setRenderTarget(null);
      gl.render(postScene, camera);
    } else {
      // Render gradient directly to the default framebuffer (skip ASCII)
      gl.setRenderTarget(null);
      gl.render(gradientScene, camera);
    }
  }, 1); // Render early so default scene (e.g., docs) draws on top

  // This component handles its own rendering, so it doesn't render any children.
  return null;
}
